import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type ARSummary, type ARSummaryCell, type ARGrade, type ARLifecycleState } from '../api/client'
import { fmtInt } from '../lib/format'

const GRADE_TONE: Record<ARGrade, string> = {
  'A': 'text-radar-green',
  'B': 'text-paper',
  'C': 'text-amber-glow',
  'D': 'text-radar-red',
  '—': 'text-paper-trace',
}

const LIFECYCLE_LABEL: Record<ARLifecycleState, string> = {
  enrolling: 'Enrolling (day 0–3)',
  in_progress: 'In progress (day 4–13)',
  settling: 'Settling (day 14–29)',
  settled: 'Settled (day 30+)',
}

const LIFECYCLE_TONE: Record<ARLifecycleState, string> = {
  enrolling: 'text-paper-trace',
  in_progress: 'text-amber-glow',
  settling: 'text-radar-cyan',
  settled: 'text-radar-green',
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
}

function fmtPct(p: number): string {
  return `${(p * 100).toFixed(0)}%`
}

function monthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

function cohortMonthsLabel(cohortMonths: string[]): string {
  if (cohortMonths.length === 0) return 'no cohort data yet'
  if (cohortMonths.length === 1) return monthLabel(cohortMonths[0]) + ' cohort'
  const sorted = [...cohortMonths].sort()
  return `${cohortMonths.length} cohorts · ${monthLabel(sorted[0])} – ${monthLabel(sorted[sorted.length - 1])}`
}

type MonthGroup = {
  cohort_month: string
  cells: ARSummaryCell[]
  size: number
  renewed: number
  renewed_pct: number
  pending: number
  in_retry: number
  terminal_fail: number
  awaiting: number
  revenue_captured: number
  revenue_at_risk: number
}

// Group the (already newest-first, slug-ordered) cells into one bucket per
// cohort month, carrying a rolled-up subtotal across all publications.
function groupByMonth(cells: ARSummaryCell[]): MonthGroup[] {
  const order: string[] = []
  const byMonth = new Map<string, MonthGroup>()
  for (const cell of cells) {
    let g = byMonth.get(cell.cohort_month)
    if (!g) {
      g = {
        cohort_month: cell.cohort_month,
        cells: [],
        size: 0, renewed: 0, renewed_pct: 0,
        pending: 0, in_retry: 0, terminal_fail: 0, awaiting: 0,
        revenue_captured: 0, revenue_at_risk: 0,
      }
      byMonth.set(cell.cohort_month, g)
      order.push(cell.cohort_month)
    }
    g.cells.push(cell)
    g.size += cell.cohort_size
    g.renewed += cell.buckets.renewed.count
    g.pending += cell.buckets.pending?.count ?? 0
    g.in_retry += cell.buckets.in_retry.count
    g.terminal_fail += cell.buckets.terminal_fail.count
    g.awaiting += cell.buckets.awaiting.count
    g.revenue_captured += cell.revenue_captured
    g.revenue_at_risk += cell.revenue_at_risk
  }
  for (const g of byMonth.values()) {
    g.renewed_pct = g.size > 0 ? g.renewed / g.size : 0
  }
  return order.map((m) => byMonth.get(m)!)
}

export function ARReportPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['ar-report-summary', 12],
    queryFn: () => api.get<ARSummary>('/api/ar-report/summary?months=12'),
    refetchInterval: 60_000,
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Auto-Renewal · Report Card</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            AR Report
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">monthly cohort grades</span>
          </h1>
          <p className="text-paper-dim mt-2 text-[13px] max-w-[800px]">
            Per-cohort renewal performance for AR-eligible subscribers. Each row is one
            month's label-pull cohort × publication; click any cell to drill into the
            cycle effectiveness, failure reasons, and per-subscriber detail. New cohort
            months are added automatically by the live registrar at each 1st-of-month
            label pull.
          </p>
        </div>
      </header>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Total cohort"
                 value={fmtInt(data.kpis.total_cohort_size)}
                 sub={cohortMonthsLabel(data.cohort_months_with_data)} />
            <KPI label="Renewed"
                 value={fmtInt(data.kpis.total_renewed)}
                 sub={fmtPct(data.kpis.renewed_pct)}
                 tone="text-radar-green" />
            <KPI label="Revenue captured"
                 value={fmtCurrency(data.kpis.revenue_captured)}
                 sub={`${fmtInt(data.kpis.total_renewed)} subs renewed`}
                 tone="text-amber-glow" />
            <KPI label="Revenue at risk"
                 value={fmtCurrency(data.kpis.revenue_at_risk)}
                 sub={`${fmtInt(data.kpis.total_in_retry + data.kpis.total_terminal_fail)} subs unresolved`}
                 tone="text-radar-red" />
          </div>

          {/* Cohort grid */}
          <section className="space-y-3">
            <h2 className="font-serif text-[22px] text-paper">Cohort grid</h2>
            {data.cells.length === 0 ? (
              <div className="label text-paper-trace p-6 border border-ink-500 bg-ink-800/40">
                No cohort data yet. Cohorts populate as the 1st-of-month label pull runs.
              </div>
            ) : (
              <div className="border border-ink-500 bg-ink-800/40 overflow-x-auto">
                <table className="w-full text-[13px] font-mono">
                  <thead>
                    <tr className="border-b border-ink-500/60 bg-ink-800/60">
                      <th className="label py-2 px-4 font-normal text-left">Cohort × Pub</th>
                      <th className="label py-2 px-3 font-normal text-right">Size</th>
                      <th className="label py-2 px-3 font-normal text-right">Renewed</th>
                      <th className="label py-2 px-3 font-normal text-right">Pending</th>
                      <th className="label py-2 px-3 font-normal text-right">In retry</th>
                      <th className="label py-2 px-3 font-normal text-right">Terminal</th>
                      <th className="label py-2 px-3 font-normal text-right">Awaiting</th>
                      <th className="label py-2 px-3 font-normal text-right">Captured</th>
                      <th className="label py-2 px-3 font-normal text-right">At risk</th>
                      <th className="label py-2 px-3 font-normal text-left">Lifecycle</th>
                      <th className="label py-2 px-3 font-normal text-center">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByMonth(data.cells).map((group) => (
                      <MonthGroupRows key={group.cohort_month} group={group} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="text-paper-trace text-[11px] font-mono pt-2">
            Grades only assigned to settled cohorts (day 30+).
            Unsettled cohorts show "—" — their final number isn't locked in yet.
          </div>
        </>
      )}
    </div>
  )
}

function KPI({ label, value, sub, tone }: {
  label: string
  value: string
  sub?: string
  tone?: string
}) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-4">
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[26px] tabular-nums ${tone || 'text-paper'}`}>{value}</div>
      {sub && <div className="text-[11px] text-paper-trace font-mono mt-1">{sub}</div>}
    </div>
  )
}

function MonthGroupRows({ group }: { group: MonthGroup }) {
  const [open, setOpen] = useState(true)
  return (
    <>
      <tr
        className="border-b border-ink-500/50 bg-ink-700/40 cursor-pointer hover:bg-ink-700/60"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-4 py-2 font-mono text-paper font-bold">
          <span className="text-paper-trace inline-block w-3 mr-1 select-none">{open ? '▾' : '▸'}</span>
          {monthLabel(group.cohort_month)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-paper font-bold">{fmtInt(group.size)}</td>
        <td className="px-3 py-2 text-right tabular-nums font-bold">
          <span className="text-radar-green">{fmtInt(group.renewed)}</span>
          <span className="text-paper-trace ml-2 text-[10px]">{fmtPct(group.renewed_pct)}</span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-radar-cyan font-bold">
          {group.pending > 0 ? fmtInt(group.pending) : <span className="text-paper-trace">—</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-amber-glow font-bold">
          {group.in_retry > 0 ? fmtInt(group.in_retry) : <span className="text-paper-trace">—</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-radar-red font-bold">
          {group.terminal_fail > 0 ? fmtInt(group.terminal_fail) : <span className="text-paper-trace">—</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-paper-dim font-bold">
          {group.awaiting > 0 ? fmtInt(group.awaiting) : <span className="text-paper-trace">—</span>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-paper-dim font-bold">{fmtCurrency(group.revenue_captured)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-radar-red/70 font-bold">{fmtCurrency(group.revenue_at_risk)}</td>
        <td className="px-3 py-2"></td>
        <td className="px-3 py-2"></td>
      </tr>
      {open && group.cells.map((cell) => (
        <CellRow key={`${cell.cohort_month}-${cell.publication_slug}`} cell={cell} />
      ))}
    </>
  )
}

function CellRow({ cell }: { cell: ARSummaryCell }) {
  const linkTo = `/ar-report/${cell.cohort_month.slice(0, 7)}/${cell.publication_slug}`
  return (
    <tr className="border-b border-ink-500/30 hover:bg-ink-700/30">
      <td className="pl-8 pr-4 py-2">
        <Link to={linkTo} className="text-paper-dim hover:text-amber-glow">
          {cell.publication_name}
        </Link>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-paper">{fmtInt(cell.cohort_size)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        <span className="text-radar-green">{fmtInt(cell.buckets.renewed.count)}</span>
        <span className="text-paper-trace ml-2 text-[10px]">{fmtPct(cell.renewed_pct)}</span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-radar-cyan">
        {(cell.buckets.pending?.count ?? 0) > 0 ? fmtInt(cell.buckets.pending.count) : <span className="text-paper-trace">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-amber-glow">
        {cell.buckets.in_retry.count > 0 ? fmtInt(cell.buckets.in_retry.count) : <span className="text-paper-trace">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-radar-red">
        {cell.buckets.terminal_fail.count > 0 ? fmtInt(cell.buckets.terminal_fail.count) : <span className="text-paper-trace">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-paper-dim">
        {cell.buckets.awaiting.count > 0 ? fmtInt(cell.buckets.awaiting.count) : <span className="text-paper-trace">—</span>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-paper-dim">{fmtCurrency(cell.revenue_captured)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-radar-red/70">{fmtCurrency(cell.revenue_at_risk)}</td>
      <td className="px-3 py-2">
        <span className={`label ${LIFECYCLE_TONE[cell.lifecycle_state]}`}>
          {LIFECYCLE_LABEL[cell.lifecycle_state]}
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <span className={`font-mono text-[18px] font-bold ${GRADE_TONE[cell.grade]}`}>{cell.grade}</span>
      </td>
    </tr>
  )
}
